import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { CaseTypeData } from '@protocols/ui';
import { CaseTypeSelectionComponent } from './case-type-selection.component';
import { LoadingOverlayComponent, PageChangeEvent, PaginatedSearchHeaderComponentModule } from '@shared/ui';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { WorkspaceEditorFacade } from './workspace-editor.facade';
import { workspaceEditorFacadeProviders } from './workspace-editor.facade.providers';

@Component({
    selector: 'ts-workspace-editor',
    templateUrl: './workspace-editor.component.html',
    styleUrls: ['./workspace-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        TranslateModule,
        CaseTypeSelectionComponent,
        PaginatedSearchHeaderComponentModule,
        LoadingOverlayComponent,
    ],
    providers: [...workspaceEditorFacadeProviders()]
})
export class WorkspaceEditorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly facade: WorkspaceEditorFacade, private readonly route: ActivatedRoute) {}

  // Facade selectors
  public get draft$() {
    return this.facade.draft$;
  }

  public get isNewWorkspace$() {
    return this.facade.isNewWorkspace$;
  }

  public get caseTypes$() {
    return this.facade.caseTypes$;
  }

  public get caseTypesLoading$() {
    return this.facade.caseTypesLoading$;
  }

  public get noCaseTypes$() {
    return this.facade.noCaseTypes$;
  }

  public get selectedCaseTypeId$() {
    return this.facade.selectedCaseTypeId$;
  }

  public get searchTerm$() {
    return this.facade.searchTerm$;
  }

  public get nextCaseTypeToken$() {
    return this.facade.nextCaseTypeToken$;
  }

  public get previousCaseTypeTokens$() {
    return this.facade.previousCaseTypeTokens$;
  }

  public get totalCount$() {
    return this.facade.totalCount$;
  }

  public get pageSize$() {
    return this.facade.pageSize$;
  }

  public get counting$() {
    return this.facade.counting$;
  }

  public get environments$() {
    return this.facade.environments$;
  }

  public get draftEnvironment$() {
    return this.facade.draftEnvironment$;
  }

  public get canSave$() {
    return this.facade.canSave$;
  }

  public get saving$() {
    return this.facade.saving$;
  }

  public get loading$() {
    return this.facade.loading$;
  }

  ngOnInit(): void {
    this.route.parent?.paramMap
      .pipe(
        map((p) => p.get('workspaceId')),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((workspaceId) => {
        if (workspaceId) {
          this.facade.initializeForEdit(workspaceId);
        } else {
          this.facade.initializeForNew();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNameChange(name: string): void {
    this.facade.updateName(name);
  }

  onCaseTypeSelected(caseType: CaseTypeData): void {
    this.facade.selectCaseType(caseType.id);
  }

  onEnvironmentChange(environment: string | undefined): void {
    this.facade.updateEnvironment(environment || undefined);
  }

  onSave(): void {
    this.facade.save();
  }

  onCancel(): void {
    this.facade.cancel();
  }

  searchTermChanged(value: string): void {
    this.facade.setSearchTerm(value);
  }

  handlePageEvent(event: PageChangeEvent<CaseTypeData>): void {
    this.facade.loadMoreCaseTypes(event.nextToken);
  }
}
